import { useState, useEffect } from 'react';
import { parseWorkoutIntoSegments, calcTotalMilesFromLog, calcXtMilesFromLog, parseDurationStringToSec } from '../../lib/workoutSegmentParser';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { HealthFlagModal } from './HealthFlagModal';
import { ZONE_ORDER, parseTimeToSeconds } from '../../lib/vdot';

const ZONE_SHORT = {
  recovery: 'Recovery', easy: 'Easy', moderate: 'Moderate',
  sub_tempo: 'Sub-T', tempo: 'Tempo', lt2: 'LT2', lt1: 'LT1',
  aerobic_power: 'A-Power', vo2max: 'VO2 Max', mixed_zone: 'Mixed',
  anaerobic_capacity: 'An-Cap', anaerobic_power: 'An-Power',
  speed_endurance: 'Speed End', speed: 'Speed',
};

function secToDisplay(sec) {
  if (!sec || sec <= 0) return '';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatCycleLabel(sec) {
  if (!sec || sec <= 0) return '';
  if (sec < 60) return `${sec}" cycle`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `${m}' cycle` : `${m}:${String(s).padStart(2, '0')} cycle`;
}

/** For cycle workouts: compute rest = cycleSeconds - split. Returns display string. */
function computeCycleRest(splitStr, cycleSeconds) {
  if (!cycleSeconds || !splitStr) return '';
  const p = String(splitStr).trim().split(':').map(Number);
  const splitSec = p.length === 2 ? p[0] * 60 + p[1] : p[0];
  if (!splitSec || isNaN(splitSec) || splitSec <= 0) return '';
  const rest = Math.round(cycleSeconds - splitSec);
  if (rest <= 0) return '';
  return secToDisplay(rest);
}

function getDistStep(unit) {
  if (unit === 'mi') return 0.25;
  if (unit === 'km') return 0.25;
  return 50; // meters
}

function Spinner({ value, min = 0, step = 1, onChange }) {
  return (
    <div className="tl-spinner">
      <button type="button" className="tl-spinner-btn" onClick={() => onChange(Math.max(min, value - step))}>−</button>
      <span className="tl-spinner-val">{value}</span>
      <button type="button" className="tl-spinner-btn" onClick={() => onChange(value + step)}>+</button>
    </div>
  );
}

function UnitSelect({ value, onChange }) {
  return (
    <select className="tl-unit-select" value={value} onChange={e => onChange(e.target.value)}>
      <option value="m">m</option>
      <option value="km">km</option>
      <option value="mi">mi</option>
    </select>
  );
}

function AutoFillRow({ paces, zone, onZoneChange, onFill, label }) {
  return (
    <div className="tl-autofill-row">
      <select className="tl-autofill-select" value={zone} onChange={e => onZoneChange(e.target.value)}>
        {ZONE_ORDER.filter(z => paces[z]).map(z => (
          <option key={z} value={z}>{paces[z].label} — {paces[z].perMile}/mi</option>
        ))}
      </select>
      <button type="button" className="tl-autofill-btn" onClick={() => onFill(zone)}>
        {label}
      </button>
    </div>
  );
}

/**
 * TrainingLogModal — step-through modal for logging an athlete's actual training.
 *
 * Props:
 *   day         — { date, dayLabel, dayNum, rawWorkout }
 *   existingLog — existing log row to pre-fill (or null for new)
 *   onSave      — callback({ segments, total_miles, notes, log_date })
 *   onClose     — dismiss without saving
 */
export function TrainingLogModal({ day, existingLog, existingAmLog, existingPmLog, startSession, onSave, onClose, onRestDay, initialAthleteRest, athleteId: athleteIdProp, paces }) {
  const { profile } = useAuth();
  const [segments, setSegments] = useState([]);
  const [step, setStep] = useState(0);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amSaved, setAmSaved] = useState(false);
  const [showAmSavedScreen, setShowAmSavedScreen] = useState(false);
  const [notes, setNotes] = useState('');
  const [showHealthFlag, setShowHealthFlag] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [autoFillZone, setAutoFillZone] = useState(() => ZONE_ORDER.find(z => paces?.[z]) ?? 'vo2max');
  // Default to rest-day view when the coach has planned rest for this
  // day — either by flipping `athlete_rest` on plan_workouts, or by
  // leaving `custom_description` blank (which the plan builder renders
  // as "Rest" by default). Only auto-triggers on full-day entries
  // (AM/PM sessions keep the normal wizard) and only when the athlete
  // hasn't already logged real workout segments.
  const [isRestDay, setIsRestDay] = useState(() => {
    if (initialAthleteRest) return true;
    const isFullDay = !startSession;
    if (!isFullDay) return false;
    const hasExistingWorkout = !!existingLog?.segments?.length;
    if (hasExistingWorkout) return false;
    return !day?.rawWorkout?.trim();
  });
  const [repDistError, setRepDistError] = useState('');

  useEffect(() => {
    let initialSegs;
    if (startSession === 'am' || startSession === 'pm') {
      // Session-specific open: only load segments for that session
      const existingSegs = startSession === 'am'
        ? (existingAmLog?.segments ?? []).map(s => ({ ...s, session: 'am' }))
        : (existingPmLog?.segments ?? []).map(s => ({ ...s, session: 'pm' }));
      if (existingSegs.length) {
        initialSegs = existingSegs;
      } else {
        const allSegs = parseWorkoutIntoSegments(day.rawWorkout ?? day.workout ?? '');
        initialSegs = allSegs.filter(s => s.session === startSession);
        if (!initialSegs.length) initialSegs = [{
          type: 'continuous', label: startSession.toUpperCase() + ' Workout',
          distance: '', distUnit: 'mi', time: '', session: startSession,
        }];
      }
      setNotes(startSession === 'pm'
        ? (existingPmLog?.notes ?? existingAmLog?.notes ?? '')
        : (existingAmLog?.notes ?? ''));
    } else if (existingAmLog || existingPmLog) {
      const amSegs = (existingAmLog?.segments ?? []).map(s => ({ ...s, session: 'am' }));
      const pmSegs = (existingPmLog?.segments ?? []).map(s => ({ ...s, session: 'pm' }));
      initialSegs = [...amSegs, ...pmSegs];
      setNotes(existingPmLog?.notes ?? existingAmLog?.notes ?? '');
      if (existingAmLog && !existingPmLog) setAmSaved(true);
    } else if (existingLog) {
      initialSegs = existingLog.segments ?? [];
      setNotes(existingLog.notes ?? '');
    } else {
      initialSegs = parseWorkoutIntoSegments(day.rawWorkout ?? day.workout ?? '');
    }
    // Auto-expand mile splits for progression runs
    initialSegs = initialSegs.map(s => {
      if (s.type === 'continuous' && s.progressionEndZone && parseFloat(s.distance) >= 1 && s.distUnit === 'mi') {
        const numMiles = Math.round(parseFloat(s.distance) || 1);
        return { ...s, splitMode: 'per-mile', mileSplits: Array(numMiles).fill('') };
      }
      return s;
    });
    setSegments(initialSegs);
  }, []);

  function updateSegment(idx, patch) {
    setSegments(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  }

  // ── Auto-fill helpers ───────────────────────────────────────────────
  function computeAutoSplit(zoneKey, distance, distUnit) {
    if (!paces || !paces[zoneKey]) return '';
    const secPerMile = parseTimeToSeconds(paces[zoneKey].perMile);
    if (!secPerMile) return '';
    const dist = parseFloat(distance);
    if (!dist) return '';
    const miles = distUnit === 'mi' ? dist : distUnit === 'km' ? dist / 1.60934 : dist / 1609.34;
    const splitSec = secPerMile * miles;
    if (splitSec < 60) return String(Math.round(splitSec));
    const m = Math.floor(splitSec / 60);
    const s = Math.round(splitSec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  function handleAutoFill(zoneKey) {
    const seg = segments[step];
    if (!seg || !paces || !paces[zoneKey]) return;
    if (seg.type === 'interval') {
      const newSplits = Array.from({ length: seg.reps }, (_, i) => {
        // Per-rep pace zones (set intervals)
        if (seg.repPaceZones?.[i]) {
          const repZone = seg.repPaceZones[i];
          if (paces[repZone]) return computeAutoSplit(repZone, seg.repDistances?.[i] || seg.distance, seg.repDistanceUnits?.[i] || seg.distUnit) || '';
        }
        // Progression intervals — interpolate between start and end zone
        if (seg.progressionEndZone && paces[seg.paceZone] && paces[seg.progressionEndZone] && seg.reps > 1) {
          const startSec = parseTimeToSeconds(paces[seg.paceZone].perMile);
          const endSec = parseTimeToSeconds(paces[seg.progressionEndZone].perMile);
          if (startSec && endSec) {
            const t = i / (seg.reps - 1);
            const interpPaceSec = startSec + t * (endSec - startSec);
            const repDist = seg.repDistances?.[i] || seg.distance;
            const repDistUnit = seg.repDistanceUnits?.[i] || seg.distUnit;
            const miles = repDistUnit === 'mi' ? repDist : repDistUnit === 'km' ? repDist / 1.60934 : repDist / 1609.34;
            const splitSec = interpPaceSec * miles;
            if (splitSec < 60) return String(Math.round(splitSec * 10) / 10);
            const m = Math.floor(splitSec / 60);
            const s = Math.round(splitSec % 60);
            return `${m}:${String(s).padStart(2, '0')}`;
          }
        }
        const repDist = seg.repDistances?.[i] || seg.distance;
        const repDistUnit = seg.repDistanceUnits?.[i] || seg.distUnit;
        return computeAutoSplit(zoneKey, repDist, repDistUnit) || '';
      });
      updateSegment(step, { splits: newSplits });
    } else if (seg.type === 'timed_interval') {
      if (seg.repPaceZones) {
        // Variable timed group — use each rep's assigned pace zone
        const newSplits = Array.from({ length: seg.reps }, (_, i) => {
          const zone = seg.repPaceZones[i] || zoneKey;
          return paces[zone] ? paces[zone].perMile + '/mi' : paces[zoneKey].perMile + '/mi';
        });
        updateSegment(step, { splits: newSplits });
      } else {
        const val = paces[zoneKey].perMile + '/mi';
        updateSegment(step, { splits: Array.from({ length: seg.reps }, () => val) });
      }
    }
  }

  function adjustReps(idx, newReps) {
    const reps = Math.max(1, Math.round(newReps));
    const seg = segments[idx];
    const splits = [...(seg.splits ?? [])];
    const rests = [...(seg.rests ?? [])];
    const repDistances = [...(seg.repDistances ?? [])];
    const repDistanceUnits = [...(seg.repDistanceUnits ?? [])];
    while (splits.length < reps) splits.push('');
    while (splits.length > reps) splits.pop();
    while (rests.length < reps) rests.push('');
    while (rests.length > reps) rests.pop();
    while (repDistances.length < reps) repDistances.push(null);
    while (repDistances.length > reps) repDistances.pop();
    while (repDistanceUnits.length < reps) repDistanceUnits.push(null);
    while (repDistanceUnits.length > reps) repDistanceUnits.pop();
    updateSegment(idx, { reps, splits, rests, repDistances, repDistanceUnits });
  }

  function setSplit(segIdx, repIdx, val) {
    const splits = [...(segments[segIdx].splits ?? [])];
    splits[repIdx] = val;
    updateSegment(segIdx, { splits });
  }

  function setRepDistance(segIdx, repIdx, val) {
    const repDistances = [...(segments[segIdx].repDistances ?? [])];
    repDistances[repIdx] = val;
    updateSegment(segIdx, { repDistances });
  }

  function setRepDistanceUnit(segIdx, repIdx, val) {
    const repDistanceUnits = [...(segments[segIdx].repDistanceUnits ?? [])];
    repDistanceUnits[repIdx] = val;
    updateSegment(segIdx, { repDistanceUnits });
  }

  function setActualRest(segIdx, repIdx, val) {
    const rests = [...(segments[segIdx].rests ?? [])];
    rests[repIdx] = val;
    updateSegment(segIdx, { rests });
  }

  function setMileSplit(segIdx, mileIdx, val) {
    const mileSplits = [...(segments[segIdx].mileSplits ?? [])];
    mileSplits[mileIdx] = val;
    updateSegment(segIdx, { mileSplits });
  }

  function toggleMileSplits(segIdx) {
    const seg = segments[segIdx];
    if (seg.splitMode === 'per-mile') {
      updateSegment(segIdx, { splitMode: null, mileSplits: [] });
    } else {
      const numMiles = Math.round(parseFloat(seg.distance) || 1);
      updateSegment(segIdx, { splitMode: 'per-mile', mileSplits: Array(numMiles).fill('') });
    }
  }

  // ── Expected split helpers ───────────────────────────────────────────
  const METERS_PER_MILE_MODAL = 1609.344;
  function paceStrToSecModal(paceStr) {
    if (!paceStr) return null;
    const p = paceStr.split(':').map(Number);
    return p.length === 2 ? p[0] * 60 + p[1] : null;
  }
  function fmtExpectedSplit(sec, meters) {
    if (!sec || sec <= 0) return null;
    if (meters <= 500) {
      const r = Math.round(sec * 10) / 10;
      return `${r % 1 === 0 ? r : r.toFixed(1)}s`;
    }
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  function computeExpectedSplit(paceZone, distance, distUnit) {
    if (!paces || !paces[paceZone]) return null;
    const paceSec = paceStrToSecModal(paces[paceZone].perMile);
    if (!paceSec) return null;
    const dist = parseFloat(distance);
    if (!dist) return null;
    const meters = distUnit === 'mi' ? dist * METERS_PER_MILE_MODAL
      : distUnit === 'km' ? dist * 1000 : dist;
    return fmtExpectedSplit((meters / METERS_PER_MILE_MODAL) * paceSec, meters);
  }
  function computeExpectedTotal(paceZone, distance, distUnit) {
    if (!paces || !paces[paceZone]) return null;
    const paceSec = paceStrToSecModal(paces[paceZone].perMile);
    if (!paceSec) return null;
    const dist = parseFloat(distance);
    if (!dist) return null;
    const miles = distUnit === 'mi' ? dist : distUnit === 'km' ? dist / 1.60934 : dist / METERS_PER_MILE_MODAL;
    const totalSec = miles * paceSec;
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = Math.round(totalSec % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  function computeAvgPaceFromTime(time, distance, distUnit) {
    if (!time || !distance) return null;
    const totalSec = parseTimeToSeconds(time);
    if (!totalSec) return null;
    const dist = parseFloat(distance);
    if (!dist) return null;
    const miles = distUnit === 'mi' ? dist : distUnit === 'km' ? dist / 1.60934 : dist / METERS_PER_MILE_MODAL;
    const paceSec = totalSec / miles;
    const m = Math.floor(paceSec / 60);
    const s = Math.round(paceSec % 60);
    return `${m}:${String(s).padStart(2, '0')}/mi`;
  }

  async function handleSaveAm() {
    setSaving(true);
    const amSegs = segments.filter(s => s.session === 'am');
    const easyPaceSec = paces?.easy?.perMile ? parseTimeToSeconds(paces.easy.perMile) : 0;
    const amRunMiles = calcTotalMilesFromLog(amSegs);
    const amXtMiles = calcXtMilesFromLog(amSegs, easyPaceSec);
    const amLogData = {
      athlete_id: athleteIdProp ?? profile.id,
      log_date: day.date,
      session: 'am',
      segments: amSegs,
      running_miles: amRunMiles,
      xt_miles: amXtMiles,
      total_miles: amRunMiles + amXtMiles,
      notes: '',
    };
    const { error } = await supabase.from('training_logs').upsert(amLogData, { onConflict: 'athlete_id,log_date,session' });
    setSaving(false);
    if (error) { setSaveError(error.message); return; }
    setAmSaved(true);
    onSave?.({ ...amLogData });
    setShowAmSavedScreen(true);
  }

  async function handleSaveRestDay() {
    setSaving(true);
    try {
      const err = await onRestDay?.(day.date, true);
      if (err) { setSaveError(err); setSaving(false); return; }
    } catch (e) {
      setSaveError(e.message ?? 'Failed to save rest day');
      setSaving(false);
      return;
    }
    setSaving(false);
    onClose();
  }

  async function handleSave() {
    // If athlete had previously marked rest day but is now logging a workout, clear it
    if (initialAthleteRest && !isRestDay) {
      await onRestDay?.(day.date, false);
    }
    setSaving(true);
    const segsToSave = isAmPmSplit ? segments.filter(s => s.session === 'pm') : segments;
    const session = startSession ?? (isAmPmSplit ? 'pm' : 'full');
    const easyPaceSec = paces?.easy?.perMile ? parseTimeToSeconds(paces.easy.perMile) : 0;
    const runningMiles = calcTotalMilesFromLog(segsToSave);
    const xtMiles = calcXtMilesFromLog(segsToSave, easyPaceSec);
    const logData = {
      athlete_id: athleteIdProp ?? profile.id,
      log_date: day.date,
      session,
      segments: segsToSave,
      running_miles: runningMiles,
      xt_miles: xtMiles,
      total_miles: runningMiles + xtMiles,
      notes,
    };
    const { error } = await supabase
      .from('training_logs')
      .upsert(logData, { onConflict: 'athlete_id,log_date,session' });
    setSaving(false);
    if (error) {
      setSaveError(error.message);
    } else {
      setSaved(true);
      onSave?.({ ...logData });
    }
  }

  const totalSteps = segments.length;
  const isLastStep = step === totalSteps - 1;
  const isFirstStep = step === 0;
  const seg = segments[step];
  const amSegs = segments.filter(s => s.session === 'am');
  const pmSegs = segments.filter(s => s.session === 'pm');
  const isAmPmSplit = amSegs.length > 0 && pmSegs.length > 0;
  const firstPmIdx = isAmPmSplit ? segments.findIndex(s => s.session === 'pm') : -1;
  const isLastAmStep = isAmPmSplit && !amSaved && step === firstPmIdx - 1;
  const hasSessions = segments.some(s => s.session);
  const currentSession = seg?.session ?? null;

  // ── Success screen ─────────────────────────────────────────────────
  if (saved) {
    return (
      <>
        <div className="tl-modal-overlay" onClick={onClose}>
          <div className="tl-modal" onClick={e => e.stopPropagation()}>
            <div className="tl-success">
              <div className="tl-success-icon">✓</div>
              <div className="tl-success-heading">
                {athleteIdProp ? 'Log saved!' : 'Workout logged!'}
              </div>
              <div className="tl-success-body">
                {athleteIdProp
                  ? 'Training log entry saved for athlete.'
                  : 'Great work. Rest up and get ready for the next one.'}
              </div>
              <div className="tl-success-actions">
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', padding: '12px 28px' }}
                  onClick={onClose}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="hf-report-btn"
                  onClick={() => setShowHealthFlag(true)}
                >
                  🩺 {athleteIdProp ? 'Report sickness or injury for athlete' : 'Something hurt or feeling sick?'}
                </button>
              </div>
            </div>
          </div>
        </div>
        {showHealthFlag && (
          <HealthFlagModal
            flagDate={day.date}
            athleteId={athleteIdProp}
            onClose={() => setShowHealthFlag(false)}
            onSaved={() => setShowHealthFlag(false)}
          />
        )}
      </>
    );
  }

  // ── AM saved intermediate screen ────────────────────────────────────
  if (showAmSavedScreen) {
    return (
      <div className="tl-modal-overlay" onClick={onClose}>
        <div className="tl-modal" onClick={e => e.stopPropagation()}>
          <div className="tl-success">
            <div className="tl-success-icon" style={{ background: '#d97706' }}>✓</div>
            <div className="tl-success-heading">AM Session Saved</div>
            <div className="tl-success-body" style={{ color: '#92400e', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 16px', marginBottom: 8 }}>
              PM session not yet logged — tap below to log it now.
            </div>
            <div className="tl-success-actions">
              <button
                className="btn btn-primary"
                style={{ width: '100%', padding: '12px 28px' }}
                onClick={() => { setShowAmSavedScreen(false); setStep(firstPmIdx); }}
              >
                Log PM Session →
              </button>
              <button
                type="button"
                className="tl-btn-back"
                style={{ marginTop: 8, width: '100%', textAlign: 'center' }}
                onClick={onClose}
              >
                Done for now (log PM later)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main modal ─────────────────────────────────────────────────────
  return (
    <div className="tl-modal-overlay" onClick={onClose}>
      <div className="tl-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="tl-modal-header">
          <div>
            <div className="tl-modal-title">{day.dayLabel} · {day.dayNum}</div>
            <div className="tl-modal-step-count">
              {startSession
                ? `${startSession.toUpperCase()} · Step ${step + 1} of ${totalSteps}`
                : hasSessions && currentSession
                  ? `${currentSession.toUpperCase()} · Step ${step + 1} of ${totalSteps}`
                  : totalSteps > 1 ? `Step ${step + 1} of ${totalSteps}` : 'Log Workout'}
            </div>
          </div>
          <button className="day-modal-close" type="button" onClick={onClose}>×</button>
        </div>

        {/* Step dots */}
        {totalSteps > 1 && (
          <div className="tl-step-dots">
            {segments.map((s, i) => (
              <div
                key={i}
                className={`tl-step-dot${i === step ? ' tl-step-dot--active' : i < step ? ' tl-step-dot--done' : ''}${s.session === 'pm' ? ' tl-step-dot--pm' : ''}`}
                onClick={() => setStep(i)}
                title={`${s.session ? s.session.toUpperCase() + ': ' : ''}${segments[i].label}`}
              />
            ))}
          </div>
        )}

        {/* Body */}
        <div className="tl-modal-body">
          {isRestDay ? (
            <div className="tl-rest-day-view">
              <div className="tl-rest-day-icon">🛌</div>
              <div className="tl-rest-day-title">Rest Day</div>
              <div className="tl-rest-day-sub">No workout to log — rest day will be noted for your coach.</div>
              <button
                type="button"
                className="tl-rest-day-undo"
                onClick={() => setIsRestDay(false)}
              >
                Log a workout instead →
              </button>
            </div>
          ) : (
          <>
          {seg && (
            <>
              {(startSession || currentSession) && (
                <div className={`tl-session-badge tl-session-badge--${startSession ?? currentSession}`}>
                  {(startSession ?? currentSession).toUpperCase()} Session
                </div>
              )}
              <div className="tl-segment-label">
                {seg.type !== 'gc' && !seg.repPaceZones && seg.paceZone && paces?.[seg.paceZone] ? (() => {
                  const dist = parseFloat(seg.distance);
                  const meters = dist
                    ? (seg.distUnit === 'mi' ? dist * METERS_PER_MILE_MODAL
                      : seg.distUnit === 'km' ? dist * 1000 : dist)
                    : null;
                  const paceDisplay = meters && meters <= 2400
                    ? (computeExpectedSplit(seg.paceZone, seg.distance, seg.distUnit) ?? `${paces[seg.paceZone].perMile}/mi`)
                    : `${paces[seg.paceZone].perMile}/mi`;
                  // If the label is generic (e.g. "Easy Jog") but the zone is harder than easy, use "Workout"
                  const label = (seg.label === 'Easy Jog' && !['recovery', 'easy'].includes(seg.paceZone))
                    ? 'Workout' : seg.label;
                  return `${label} @ ${paceDisplay}`;
                })() : seg.label}
              </div>

              {/* ── Interval set ──────────────────────────────────── */}
              {seg.type === 'interval' && (
                <>
                  {seg.repPaceZones ? (
                    /* Set interval — show per-distance targets */
                    paces && (() => {
                      // Dedupe the unique distance+zone combos for the target display
                      const seen = new Set();
                      const targets = [];
                      for (let i = 0; i < (seg.repDistances?.length ?? 0); i++) {
                        const key = `${seg.repDistances[i]}-${seg.repDistanceUnits?.[i]}-${seg.repPaceZones?.[i]}`;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        const zone = seg.repPaceZones[i];
                        const dist = seg.repDistances[i];
                        const unit = seg.repDistanceUnits?.[i] || 'm';
                        const split = zone && paces[zone] ? computeExpectedSplit(zone, dist, unit) : null;
                        targets.push({ dist, unit, zone, split });
                      }
                      return targets.length > 0 ? (
                        <div className="tl-target-chip" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                          {targets.map((t, i) => (
                            <div key={i}>
                              <strong>{t.dist}{t.unit}</strong>
                              {t.zone && paces[t.zone] && <> — {ZONE_SHORT[t.zone] ?? t.zone} @ {t.split ?? paces[t.zone].perMile + '/mi'}</>}
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()
                  ) : (
                    /* Standard interval — single target or progression */
                    seg.paceZone && paces?.[seg.paceZone] && (() => {
                      if (seg.progressionEndZone && paces[seg.progressionEndZone]) {
                        const startSplit = computeExpectedSplit(seg.paceZone, seg.distance, seg.distUnit);
                        const endSplit = computeExpectedSplit(seg.progressionEndZone, seg.distance, seg.distUnit);
                        return (
                          <div className="tl-target-chip">
                            Progression: <strong>{startSplit ?? paces[seg.paceZone].perMile + '/mi'}</strong> → <strong>{endSplit ?? paces[seg.progressionEndZone].perMile + '/mi'}</strong>
                          </div>
                        );
                      }
                      const expectedSplit = computeExpectedSplit(seg.paceZone, seg.distance, seg.distUnit);
                      return expectedSplit ? (
                        <div className="tl-target-chip">
                          Target: <strong>{expectedSplit}</strong> per rep · {paces[seg.paceZone].perMile}/mi
                        </div>
                      ) : null;
                    })()
                  )}
                  {paces && (
                    <AutoFillRow paces={paces} zone={autoFillZone} onZoneChange={setAutoFillZone} onFill={handleAutoFill}
                      label={seg.repPaceZones ? 'Auto-fill (per zone)' : 'Auto-fill Splits'} />
                  )}
                  <div className="tl-controls-row">
                    {!seg.repPaceZones && (
                      <div className="tl-control-group">
                        <label className="tl-control-label">Reps</label>
                        <Spinner
                          value={seg.reps}
                          min={1}
                          step={1}
                          onChange={v => adjustReps(step, v)}
                        />
                      </div>
                    )}
                    {seg.repPaceZones ? (
                      <div className="tl-control-group">
                        <label className="tl-control-label">Reps</label>
                        <span className="tl-spinner-val" style={{ padding: '0 12px', lineHeight: '36px' }}>{seg.reps}</span>
                      </div>
                    ) : (
                      <div className="tl-control-group">
                        <label className="tl-control-label">Distance</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Spinner
                            value={seg.distance}
                            min={getDistStep(seg.distUnit)}
                            step={getDistStep(seg.distUnit)}
                            onChange={v => updateSegment(step, { distance: v })}
                          />
                          <UnitSelect value={seg.distUnit} onChange={v => updateSegment(step, { distUnit: v })} />
                        </div>
                      </div>
                    )}
                    <div className="tl-control-group">
                      <label className="tl-control-label">Rest</label>
                      {seg.restType === 'jog_down' ? (
                        <span className="tl-spinner-val" style={{ padding: '0 12px', lineHeight: '36px', color: 'var(--gray-400)' }}>
                          Jog ↓
                        </span>
                      ) : seg.restType === 'cycle' ? (
                        <span className="tl-spinner-val" style={{ padding: '0 12px', lineHeight: '36px' }}>
                          {formatCycleLabel(seg.cycleSeconds)}
                        </span>
                      ) : seg.restType === 'equal' ? (
                        <span className="tl-spinner-val" style={{ padding: '0 12px', lineHeight: '36px' }}>
                          {(() => {
                            if (!seg.paceZone || !paces?.[seg.paceZone]) return 'Equal rest';
                            const split = computeExpectedSplit(seg.paceZone, seg.distance, seg.distUnit);
                            if (!split) return 'Equal rest';
                            // Parse split string to seconds and round
                            const parts = split.replace('s', '').split(':').map(Number);
                            const sec = parts.length === 2 ? parts[0] * 60 + parts[1] : parts[0];
                            return secToDisplay(Math.round(sec));
                          })()}
                        </span>
                      ) : (
                        <>
                          <Spinner
                            value={seg.rest}
                            min={0}
                            step={15}
                            onChange={v => updateSegment(step, { rest: v })}
                          />
                          <span className="tl-rest-sec-label">{secToDisplay(seg.rest)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="tl-splits-header">
                    <span>Rep</span>
                    {!seg.progressionEndZone && <span>Dist</span>}
                    <span>Split</span>
                    <span>
                      {seg.restType === 'jog_down' ? 'Recovery' : seg.restType === 'cycle' || seg.restType === 'equal' ? 'Rest (auto)' : 'Actual rest'}
                    </span>
                    {seg.progressionEndZone && paces?.[seg.progressionEndZone] && <span>Target</span>}
                  </div>
                  <div className="tl-splits-list">
                    {Array.from({ length: seg.reps }, (_, i) => {
                      // Compute interpolated target for progression intervals
                      let progTarget = null;
                      if (seg.progressionEndZone && paces?.[seg.paceZone] && paces?.[seg.progressionEndZone] && seg.reps > 1) {
                        const startSec = paceStrToSecModal(paces[seg.paceZone].perMile);
                        const endSec = paceStrToSecModal(paces[seg.progressionEndZone].perMile);
                        if (startSec && endSec) {
                          const t = i / (seg.reps - 1);
                          const interpPaceSec = startSec + t * (endSec - startSec);
                          const repDist = parseFloat(seg.repDistances?.[i] || seg.distance) || 0;
                          const repUnit = seg.repDistanceUnits?.[i] || seg.distUnit;
                          const miles = repUnit === 'mi' ? repDist : repUnit === 'km' ? repDist / 1.60934 : repDist / METERS_PER_MILE_MODAL;
                          const splitSec = interpPaceSec * miles;
                          if (splitSec < 60) {
                            const r = Math.round(splitSec * 10) / 10;
                            progTarget = `${r % 1 === 0 ? r : r.toFixed(1)}s`;
                          } else {
                            const m = Math.floor(splitSec / 60);
                            const s = Math.round(splitSec % 60);
                            progTarget = `${m}:${String(s).padStart(2, '0')}`;
                          }
                        }
                      }
                      return (
                      <div key={i} className="tl-split-row">
                        <span className="tl-split-num">{i + 1}</span>
                        {!seg.progressionEndZone && (
                        <div className="tl-rep-dist-wrap">
                          <input
                            className={`tl-rep-dist-input${seg.repDistances?.[i] === '' ? ' tl-rep-dist-input--error' : ''}`}
                            value={seg.repDistances?.[i] ?? ''}
                            placeholder={String(seg.distance)}
                            onChange={e => { setRepDistError(''); setRepDistance(step, i, e.target.value); }}
                          />
                          <UnitSelect
                            value={seg.repDistanceUnits?.[i] ?? seg.distUnit}
                            onChange={v => setRepDistanceUnit(step, i, v)}
                          />
                        </div>
                        )}
                        <input
                          className="tl-split-input"
                          placeholder={progTarget ?? (seg.distance <= 400 && seg.distUnit === 'm' ? 'e.g. 34' : 'e.g. 2:04')}
                          value={seg.splits?.[i] ?? ''}
                          onChange={e => setSplit(step, i, e.target.value)}
                          autoFocus={i === 0}
                        />
                        {seg.restType === 'jog_down' ? (
                          <span className="tl-rest-last" style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>jog ↓</span>
                        ) : seg.restType === 'cycle' ? (
                          <span className="tl-rest-cycle">
                            {computeCycleRest(seg.splits?.[i], seg.cycleSeconds) || '—'}
                          </span>
                        ) : seg.restType === 'equal' ? (
                          <span className="tl-rest-cycle">
                            {(() => {
                              // Use athlete's actual split if entered, otherwise expected split
                              if (seg.splits?.[i]) {
                                const s = String(seg.splits[i]).trim();
                                const p = s.split(':').map(Number);
                                const sec = p.length === 2 && !p.some(isNaN) ? p[0] * 60 + p[1] : parseFloat(s);
                                return !isNaN(sec) && sec > 0 ? secToDisplay(Math.round(sec)) : '—';
                              }
                              // Fall back to expected split from pace zone
                              if (seg.paceZone && paces?.[seg.paceZone]) {
                                const repDist = seg.repDistances?.[i] || seg.distance;
                                const repUnit = seg.repDistanceUnits?.[i] || seg.distUnit;
                                const split = computeExpectedSplit(seg.paceZone, repDist, repUnit);
                                if (split) {
                                  const parts = split.replace('s', '').split(':').map(Number);
                                  const sec = parts.length === 2 ? parts[0] * 60 + parts[1] : parts[0];
                                  return secToDisplay(Math.round(sec));
                                }
                              }
                              return '—';
                            })()}
                          </span>
                        ) : i < seg.reps - 1 ? (
                          <input
                            className="tl-rest-input"
                            placeholder={secToDisplay(seg.rest)}
                            value={seg.rests?.[i] ?? ''}
                            onChange={e => setActualRest(step, i, e.target.value)}
                          />
                        ) : (
                          <span className="tl-rest-last">—</span>
                        )}
                        {seg.progressionEndZone && progTarget && <span className="tl-target-val">{progTarget}</span>}
                      </div>
                      );
                    })}
                  </div>
                  {repDistError && (
                    <div className="tl-rep-dist-error">{repDistError}</div>
                  )}
                </>
              )}

              {/* ── Timed interval set ────────────────────────────── */}
              {seg.type === 'timed_interval' && (
                <>
                  {seg.repDurations && seg.repPaceZones ? (
                    /* Variable-duration timed group — (1'[LT2], 1'[RECOVERY], 2'[LT2], ...) */
                    <>
                      {paces && (
                        <div style={{ padding: '8px 0 12px' }}>
                          <button
                            type="button"
                            className="tl-autofill-btn"
                            style={{ padding: '8px 16px', fontSize: 13 }}
                            onClick={() => handleAutoFill(autoFillZone)}
                          >
                            Auto-fill targets
                          </button>
                        </div>
                      )}
                      <div className="tl-splits-header" style={{ gridTemplateColumns: '30px 50px 1fr 1fr' }}>
                        <span>Rep</span>
                        <span>Time</span>
                        <span>Zone</span>
                        <span>Pace</span>
                      </div>
                      <div className="tl-splits-list">
                        {Array.from({ length: seg.reps }, (_, i) => {
                          const dur = seg.repDurations?.[i] ?? seg.duration;
                          const zone = seg.repPaceZones?.[i];
                          const zoneName = zone && ZONE_SHORT[zone] ? ZONE_SHORT[zone] : '';
                          const targetPace = zone && paces?.[zone] ? paces[zone].perMile + '/mi' : null;
                          return (
                            <div key={i} className="tl-split-row" style={{ gridTemplateColumns: '30px 50px 1fr 1fr' }}>
                              <span className="tl-split-num">{i + 1}</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>{secToDisplay(dur)}</span>
                              <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>{zoneName}{targetPace ? ` ${targetPace}` : ''}</span>
                              <input
                                className="tl-split-input"
                                placeholder={targetPace ?? 'e.g. 6:30/mi'}
                                value={seg.splits?.[i] ?? ''}
                                onChange={e => setSplit(step, i, e.target.value)}
                                autoFocus={i === 0}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    /* Uniform timed interval — NxM'[TAG] on M'[TAG] */
                    <>
                      {seg.paceZone && paces?.[seg.paceZone] && (
                        <div className="tl-target-chip">
                          Target: <strong>{paces[seg.paceZone].perMile}/mi</strong> on
                          {seg.restZone && paces?.[seg.restZone] && (
                            <> · <strong>{paces[seg.restZone].perMile}/mi</strong> off</>
                          )}
                        </div>
                      )}
                      {paces && (
                        <AutoFillRow paces={paces} zone={autoFillZone} onZoneChange={setAutoFillZone} onFill={handleAutoFill} label="Auto-fill Pace" />
                      )}
                      <div className="tl-controls-row">
                        <div className="tl-control-group">
                          <label className="tl-control-label">Reps</label>
                          <Spinner value={seg.reps} min={1} step={1} onChange={v => adjustReps(step, v)} />
                        </div>
                        <div className="tl-control-group">
                          <label className="tl-control-label">Duration</label>
                          <span className="tl-spinner-val" style={{ padding: '0 12px', lineHeight: '36px' }}>
                            {secToDisplay(seg.duration)}
                          </span>
                        </div>
                        <div className="tl-control-group">
                          <label className="tl-control-label">Recovery</label>
                          <span className="tl-spinner-val" style={{ padding: '0 12px', lineHeight: '36px' }}>
                            {secToDisplay(seg.rest)}
                          </span>
                        </div>
                      </div>
                      <div className="tl-splits-header">
                        <span>Rep</span>
                        <span>Pace</span>
                        <span>Recovery pace</span>
                      </div>
                      <div className="tl-splits-list">
                        {Array.from({ length: seg.reps }, (_, i) => (
                          <div key={i} className="tl-split-row">
                            <span className="tl-split-num">{i + 1}</span>
                            <input
                              className="tl-split-input"
                              placeholder="e.g. 6:30/mi"
                              value={seg.splits?.[i] ?? ''}
                              onChange={e => setSplit(step, i, e.target.value)}
                              autoFocus={i === 0}
                            />
                            {i < seg.reps - 1 ? (
                              <input
                                className="tl-rest-input"
                                placeholder="e.g. 8:30/mi"
                                value={seg.rests?.[i] ?? ''}
                                onChange={e => setActualRest(step, i, e.target.value)}
                              />
                            ) : (
                              <span className="tl-rest-last">—</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ── GC (Gear Change) ──────────────────────────────── */}
              {seg.type === 'gc' && (
                <div className="tl-continuous-form">
                  {/* Expected splits chip */}
                  {seg.gcParts && paces && (() => {
                    const targets = seg.gcParts.map(p =>
                      p.paceZone ? computeExpectedSplit(p.paceZone, p.dist, p.distUnit) : null
                    );
                    if (!targets.some(Boolean)) return null;
                    const totalSec = seg.gcParts.reduce((sum, p) => {
                      if (!p.paceZone || !paces[p.paceZone]) return sum;
                      const paceSec = paceStrToSecModal(paces[p.paceZone].perMile);
                      if (!paceSec) return sum;
                      const meters = p.distUnit === 'mi' ? p.dist * METERS_PER_MILE_MODAL
                        : p.distUnit === 'km' ? p.dist * 1000 : p.dist;
                      return sum + (meters / METERS_PER_MILE_MODAL) * paceSec;
                    }, 0);
                    const tm = Math.floor(totalSec / 60);
                    const ts = Math.round(totalSec % 60);
                    const totalStr = totalSec > 0 ? ` · ${tm}:${String(ts).padStart(2, '0')} total` : '';
                    return (
                      <div className="tl-target-chip">
                        Expected: <strong>{targets.map(t => t ?? '—').join(' / ')}{totalStr}</strong>
                      </div>
                    );
                  })()}

                  {/* Per-segment split inputs */}
                  <div className="tl-splits-header" style={{ gridTemplateColumns: '3fr 2fr 2fr' }}>
                    <span>Segment</span>
                    <span>Split</span>
                    <span>Target</span>
                  </div>
                  <div className="tl-splits-list">
                    {(seg.gcParts ?? []).map((part, i) => {
                      const target = part.paceZone ? computeExpectedSplit(part.paceZone, part.dist, part.distUnit) : null;
                      const partLabel = part.dist != null ? `${part.dist}${part.distUnit}` : `Seg ${i + 1}`;
                      const placeholder = target ?? (part.distUnit === 'm' && part.dist <= 400 ? 'e.g. 56s' : 'e.g. 1:26');
                      return (
                        <div key={i} className="tl-split-row" style={{ gridTemplateColumns: '3fr 2fr 2fr' }}>
                          <span className="tl-split-num" style={{ fontWeight: 600, fontSize: '0.85rem' }}>{partLabel}</span>
                          <input
                            className="tl-split-input"
                            placeholder={placeholder}
                            value={seg.splits?.[i] ?? ''}
                            onChange={e => setSplit(step, i, e.target.value)}
                            autoFocus={i === 0}
                          />
                          {target ? <span className="tl-target-val">{target}</span> : <span />}
                        </div>
                      );
                    })}
                  </div>

                  {/* Total time */}
                  <div className="tl-control-group" style={{ marginTop: 12 }}>
                    <label className="tl-control-label">Total time (optional)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        className="tl-continuous-input"
                        placeholder="4:27"
                        value={seg.time}
                        onChange={e => updateSegment(step, { time: e.target.value })}
                      />
                      {(() => {
                        const avgPace = computeAvgPaceFromTime(seg.time, seg.distance, seg.distUnit);
                        return avgPace ? <span className="tl-avg-pace-display">{avgPace}</span> : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Continuous run ────────────────────────────────── */}
              {seg.type === 'continuous' && (
                <div className="tl-continuous-form">
                  {seg.paceZone && paces?.[seg.paceZone] && (() => {
                    if (seg.progressionEndZone && paces[seg.progressionEndZone]) {
                      return (
                        <div className="tl-target-chip">
                          Progression: <strong>{paces[seg.paceZone].perMile}/mi</strong> → <strong>{paces[seg.progressionEndZone].perMile}/mi</strong>
                        </div>
                      );
                    }
                    const dist = parseFloat(seg.distance);
                    const meters = dist
                      ? (seg.distUnit === 'mi' ? dist * METERS_PER_MILE_MODAL
                        : seg.distUnit === 'km' ? dist * 1000 : dist)
                      : null;
                    if (meters && meters <= 2400) return null;
                    const expectedTotal = computeExpectedTotal(seg.paceZone, seg.distance, seg.distUnit);
                    if (!expectedTotal) return null;
                    return (
                      <div className="tl-target-chip">
                        Est. <strong>{expectedTotal}</strong> total
                      </div>
                    );
                  })()}
                  <div className="tl-control-group">
                    <label className="tl-control-label">Distance</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        className="tl-continuous-input"
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="0.0"
                        value={seg.distance}
                        onChange={e => updateSegment(step, { distance: e.target.value })}
                      />
                      <UnitSelect value={seg.distUnit} onChange={v => updateSegment(step, { distUnit: v })} />
                    </div>
                  </div>
                  <div className="tl-control-group">
                    <label className="tl-control-label">Total time (optional)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        className="tl-continuous-input"
                        placeholder="12:30"
                        value={seg.time}
                        onChange={e => updateSegment(step, { time: e.target.value })}
                      />
                      {(() => {
                        const avgPace = computeAvgPaceFromTime(seg.time, seg.distance, seg.distUnit);
                        return avgPace ? <span className="tl-avg-pace-display">{avgPace}</span> : null;
                      })()}
                    </div>
                  </div>
                  {seg.paceZone && !['recovery', 'easy'].includes(seg.paceZone) && parseFloat(seg.distance) >= 1 && seg.distUnit === 'mi' && (
                    <>
                      <button
                        type="button"
                        className="tl-mile-splits-toggle"
                        onClick={() => toggleMileSplits(step)}
                      >
                        {seg.splitMode === 'per-mile' ? '▲ Log total time only' : '▼ Log mile splits'}
                      </button>
                      {seg.splitMode === 'per-mile' && (
                        <div className="tl-mile-splits">
                          <div className="tl-splits-header" style={{ gridTemplateColumns: '2.5rem 1fr 1fr' }}>
                            <span>Mile</span>
                            <span>Split</span>
                            {seg.paceZone && paces?.[seg.paceZone] && <span>Target</span>}
                          </div>
                          <div className="tl-splits-list">
                            {(seg.mileSplits ?? []).map((val, i) => {
                              const numMiles = (seg.mileSplits ?? []).length;
                              let target;
                              if (seg.progressionEndZone && paces?.[seg.progressionEndZone] && numMiles > 1) {
                                // Interpolate between start and end zone paces
                                const startSec = paceStrToSecModal(paces[seg.paceZone]?.perMile);
                                const endSec = paceStrToSecModal(paces[seg.progressionEndZone]?.perMile);
                                if (startSec && endSec) {
                                  const t = i / (numMiles - 1);
                                  const interpSec = Math.round(startSec + t * (endSec - startSec));
                                  const m = Math.floor(interpSec / 60);
                                  const s = Math.round(interpSec % 60);
                                  target = `${m}:${String(s).padStart(2, '0')}`;
                                }
                              } else {
                                target = computeExpectedSplit(seg.paceZone, 1, 'mi');
                              }
                              return (
                                <div key={i} className="tl-split-row" style={{ gridTemplateColumns: '2.5rem 1fr 1fr' }}>
                                  <span className="tl-split-num">{i + 1}</span>
                                  <input
                                    className="tl-split-input"
                                    placeholder="e.g. 5:24"
                                    value={val}
                                    onChange={e => setMileSplit(step, i, e.target.value)}
                                    autoFocus={i === 0}
                                  />
                                  {target && <span className="tl-target-val">{target}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Checkbox activity ─────────────────────────────── */}
              {seg.type === 'checkbox' && (
                <div className="tl-checkbox-segment">
                  <button
                    type="button"
                    className={`tl-checkbox-toggle${seg.checked ? ' tl-checkbox-toggle--checked' : ''}`}
                    onClick={() => updateSegment(step, { checked: !seg.checked })}
                  >
                    <span className="tl-checkbox-icon">{seg.checked ? '✓' : ''}</span>
                    {seg.checked ? 'Completed' : 'Tap to mark complete'}
                  </button>
                </div>
              )}

              {/* ── Cross-training ────────────────────────────────── */}
              {seg.type === 'xt' && (
                <div className="tl-continuous-form">
                  <div className="tl-control-group">
                    <label className="tl-control-label">Duration</label>
                    <input
                      className="tl-continuous-input"
                      placeholder="45 min or 45'"
                      value={seg.duration}
                      onChange={e => updateSegment(step, { duration: e.target.value })}
                    />
                    {(() => {
                      if (!paces?.easy?.perMile || !seg.duration) return null;
                      const sec = parseDurationStringToSec(seg.duration);
                      if (!sec) return null;
                      const easyPaceSec = parseTimeToSeconds(paces.easy.perMile);
                      if (!easyPaceSec) return null;
                      const xtMi = (sec / easyPaceSec).toFixed(1);
                      return (
                        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                          ≈ {xtMi} mi equivalent @ {paces.easy.perMile}/mi easy pace
                        </div>
                      );
                    })()}
                  </div>
                  <div className="tl-control-group">
                    <label className="tl-control-label">Avg HR (optional)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        className="tl-continuous-input"
                        type="number"
                        min="40"
                        max="220"
                        placeholder="145"
                        value={seg.avgHr ?? ''}
                        onChange={e => updateSegment(step, { avgHr: e.target.value })}
                      />
                      <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>bpm</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes on last step */}
              {isLastStep && (
                <div className="tl-notes-section">
                  <label className="tl-control-label">Notes (optional)</label>
                  <textarea
                    className="tl-notes-textarea"
                    placeholder="How did it feel?"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </>
          )}

          {/* Rest day toggle — always visible in normal step flow */}
          <button
            type="button"
            className="tl-rest-day-toggle"
            onClick={() => setIsRestDay(true)}
          >
            🛌 Took a rest day instead
          </button>
          </>)}
        </div>

        {/* Footer */}
        {saveError && (
          <div style={{ padding: '0 24px 8px', color: '#dc2626', fontSize: 13 }}>
            Save failed: {saveError}
          </div>
        )}
        <div className="tl-modal-footer">
          {isRestDay ? (
            <>
              <button type="button" className="tl-btn-back" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: 'auto', padding: '10px 24px' }}
                onClick={handleSaveRestDay}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Rest Day'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="tl-btn-back"
                onClick={() => setStep(s => s - 1)}
                style={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
              >
                ← Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: 'auto', padding: '10px 24px' }}
                onClick={() => {
                  const seg = segments[step];
                  if (seg?.type === 'interval') {
                    const hasBlank = (seg.repDistances ?? []).some(d => d === '');
                    if (hasBlank) {
                      setRepDistError('Each rep needs a distance — enter one or clear to use the default.');
                      return;
                    }
                  }
                  setRepDistError('');
                  if (isLastAmStep) handleSaveAm();
                  else if (isLastStep) handleSave();
                  else setStep(s => s + 1);
                }}
                disabled={saving}
              >
                {saving
                  ? 'Saving...'
                  : isLastAmStep
                    ? 'Save AM & Continue →'
                    : isLastStep
                      ? (startSession === 'am' ? 'Save AM Session'
                        : startSession === 'pm' ? 'Save PM Session'
                        : isAmPmSplit ? 'Save PM Session'
                        : 'Save Workout')
                      : 'Next →'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
